import { useEffect, useState } from "react";
import { classNames } from "@NBCUDTC/reactjs-utility-belt/classnames";

import styles from "./Image.module.scss";
import { ImageProps } from "./types";

/**
 * Image component that renders an image with customizable styling and attributes.
 * Automatically provides SVG coloring support by rendering SVGs inline.
 *
 * @remarks
 * When used with SVG images, this component:
 * - Fetches and inlines the SVG content
 * - Replaces all fill/stroke attributes with currentColor
 * - Applies the color prop through the CSS color property
 *
 * For non-SVG images, it behaves like a standard img element.
 *
 * @example
 * ```jsx
 * // Basic usage
 * <Image src="/logo.png" alt="Logo" />
 *
 * // SVG with color
 * <Image src="/icons/warning.svg" color="#FF0000" />
 *
 * // SVG with CSS variable
 * <Image src="/icons/warning.svg" color="var(--warning-color)" />
 *
 * // SVG with CSS class (color can be set in stylesheet)
 * <Image src="/icons/warning.svg" className="warning-icon" />
 * ```
 *
 * @param className - Optional additional CSS class names to apply
 * @param src - The source URL of the image
 * @param color - The color the SVG should be
 * @param allowColorOverride - Allows SVGs to change fill and stroke to current or specified color
 * @param alt - Alternative text for the image (for accessibility)
 * @param style - Additional inline styles to apply
 * @param attrs - Additional HTML attributes for the image element
 *
 * @returns A styled image element or inline SVG with proper coloring
 */
const Image = ({
  className,
  src,
  color,
  allowColorOverride = true,
  ...attrs
}: ImageProps) => {
  // Generate combined class names using utility function
  const cn = classNames(styles["image"], className);

  // Extract common props with defaults from attributes
  const { alt = "", style = {}, ...restAttrs } = attrs;

  // State to track SVG content and loading status
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [fetchAttempted, setFetchAttempted] = useState<boolean>(false);

  // Determine if the source is an SVG by file extension
  const isSvg = src?.toLowerCase().endsWith(".svg");

  /**
   * Fetches and processes SVG content from the source URL
   * Sets up SVG for inline rendering with currentColor support
   */
  useEffect(() => {
    // Skip if not an SVG, no source, or already attempted
    if (!isSvg || !src || fetchAttempted) return;

    const fetchSvg = async () => {
      try {
        setFetchAttempted(true);

        // Fetch the SVG content
        const response = await fetch(src);
        const svgText = await response.text();

        // Parse the SVG as XML
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
        const svgElement = svgDoc.documentElement;

        // Make SVG responsive by removing fixed dimensions
        svgElement.removeAttribute("width");
        svgElement.removeAttribute("height");
        svgElement.setAttribute("preserveAspectRatio", "xMidYMid meet");

        // Store the processed SVG content
        setSvgContent(svgElement.outerHTML);
      } catch (error) {
        console.error("Error loading SVG:", error);
        setSvgContent(null);
      }
    };

    fetchSvg();
  }, [src, isSvg, fetchAttempted]);

  /**
   * Reset state when source changes to ensure proper reloading
   */
  useEffect(() => {
    setFetchAttempted(false);
    setSvgContent(null);
  }, [src]);

  // Return nothing if there is no src
  if (!src) {
    return null;
  }

  // Render inline SVG with currentColor support if available
  if (isSvg && svgContent) {
    // Replace all fill and stroke values with currentColor for CSS control
    let styledSvg = svgContent.replace(
      /<svg/g,
      '<svg style="width:100%;height:100%;"',
    );

    if (allowColorOverride) {
      styledSvg = styledSvg
        .replace(/fill="(?!none)[^"]*"/g, 'fill="currentColor"')
        .replace(/stroke="(?!none)[^"]*"/g, 'stroke="currentColor"');
    }

    return (
      <div
        className={cn}
        dangerouslySetInnerHTML={{ __html: styledSvg }}
        role="img"
        aria-label={alt}
        style={{
          ...style,
          ...(color && { color }),
        }}
        {...restAttrs}
      />
    );
  }

  // Render standard image for non-SVGs or if SVG loading failed
  return (
    <img
      className={cn}
      src={src}
      loading="lazy"
      alt={alt}
      style={style}
      {...restAttrs}
    />
  );
};

export { Image };
